import React, {useEffect, useState} from 'react';
import './GlobalCPC.page.css';
import CosiMap from './CosiMap.comp';
import backgroundImage from "../../assets/images/bg7.jpg";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import SunburstHighChart from "./SunburstHighChart.comp";
import CountryRanking from "./CountryRanking.comp";
import {getGoalsIndexAllDataService} from "../../services/elevate_cpc.service";
import {countries} from "../../utils/Countries";

const GlobalCPC = () => {

    // const [countriesCodes, setCountriesCodes] = useState([]);
    const [globalGoalsChartData, setGlobalGoalsChartData] = useState({});
    const [globalGoalsTableData, setGlobalGoalsTableData] = useState([]);
    const [displaySunburstRoot, setDisplaySunburstRoot] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const {data} = await getGoalsIndexAllDataService();
                if (data && data.indexData) {
                    const filteredCountries = countries.filter(country =>
                        Object.prototype.hasOwnProperty.call(data.indexData, country.code)
                    );

                    const countriesTableData = filteredCountries.map(country => ({
                        flag: country.code,
                        name: country.label,
                        level: data.indexData[country.code] || "None"
                    }));
                    setGlobalGoalsTableData(countriesTableData);

                    const chartData = filteredCountries.reduce((acc, country) => {
                        acc[country.code] = data.indexData[country.code] || "None";
                        return acc;
                    }, {});
                    setGlobalGoalsChartData(chartData);
                }
            } catch (e) {
                console.error(e);
            }
        };
        fetchData();
    }, []);

    const displayChartRoot = (display) => {
        setDisplaySunburstRoot(display);
    }

    return (
        <>
            <Box sx={{marginTop: '80px'}}>
                <CosiMap globalGoalsChartData={globalGoalsChartData}/>
            </Box>
            <Box sx={{marginTop: '80px', marginBottom: '80px'}}>
                <CountryRanking data={globalGoalsTableData}/>
            </Box>
            {/* section3*/}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                justifyContent: 'flex-end',
                background: `url(${backgroundImage}) center/cover no-repeat`,
                padding: '40px',
                minHeight: '100vh',
                color: 'white',
            }}>
                <Box sx={{
                    width: {xs: '100%', sm: '37%'},
                    marginRight: {xs: '0px', sm: '65px'},
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 2,
                    justifyContent: 'center',
                }}>
                    <Typography sx={{fontSize: '40px', lineHeight: '1.2', fontWeight: 'bold', marginBottom: 2,}}>
                        What is the Child Protection in Cyberspace Index (CPCI)?
                    </Typography>
                    <Typography sx={{fontSize: '20px', lineHeight: '1.1', marginBottom: 2,}}>
                        The <span style={{fontWeight: 'bold'}}>Child Protection in Cyberspace Index (CPCI)</span> is a
                        comprehensive national metric designed to help countries measure, track, and improve child
                        online protection.
                    </Typography>
                    <Typography sx={{fontSize: '20px', lineHeight: '1.1', marginBottom: 2,}}>
                        As a global benchmark, CPCI evaluates all key stakeholders in children’s digital lives—children,
                        families, schools, companies, governments, and technology infrastructure—to assess their efforts
                        and progress in strengthening child protection in cyberspace.
                    </Typography>
                    <Typography sx={{fontSize: '20px', lineHeight: '1.1',}}>
                        With a data-driven scoring system, CPCI enables nations to track progress, identify gaps, and
                        gain comparative insights. It empowers policymakers and stakeholders to make informed decisions
                        and drive targeted, continuous improvements in child protection in cyberspace.
                    </Typography>
                    <Typography sx={{fontSize: '20px', lineHeight: '1.1',}}>
                        By providing a standardized measurement approach, CPCI fosters global accountability and
                        progress in protecting children from cyber risks while promoting a safe, secure, and ethical
                        cyberspace.
                    </Typography>
                </Box>
            </Box>

            {/*section 4*/}
            <Box sx={{padding: {xs: '40px', sm: '100px 40px 40px 40px',}}}>
                <Typography sx={{
                    fontSize: '27px',
                    fontWeight: 'bold',
                    color: '#003C69',
                    textAlign: 'center',
                    marginBottom: 3
                }}>
                    The CPCI Framework
                </Typography>

                <Typography sx={{
                    fontSize: '20px',
                    lineHeight: '1.2',
                    color: '#003C69',
                    textAlign: 'left',
                    marginBottom: 4,
                    px: {xs: '20px', sm: '200px'},
                }}>
                    The CPCI Framework is a structured assessment model designed to evaluate a nation’s level of child
                    online protection and track progress toward the overarching both global and national goals. It
                    follows a hierarchical structure, ensuring a comprehensive assessment across multiple levels of
                    responsibility, from national leadership to individual stakeholder actions:
                </Typography>

                {/* Points Section */}
                <Box sx={{textAlign: 'left', px: {xs: '10px', sm: '180px'}, marginBottom: 2, marginLeft: 2}}>
                    <ul>
                        <li>
                            <Typography sx={{fontSize: '20px', color: '#003C69',}}>
                                <span style={{fontWeight: 'bold'}}>1 CPCI Vision: </span>Aiming to reduce child
                                cyber-risks by more than 15% by 2030 compared to the pre-Gen-AI era.
                            </Typography>
                        </li>
                        <li>
                            <Typography sx={{fontSize: '20px', color: '#003C69',}}>
                                <span style={{fontWeight: 'bold'}}>7 Goals: </span>Specific goals established for key
                                stakeholders— children, families, schools, companies, governments, and technology
                                infrastructure—to drive progress toward the CPCI Vision.
                            </Typography>
                        </li>
                        <li>
                            <Typography sx={{fontSize: '20px', color: '#003C69',}}>
                                <span style={{fontWeight: 'bold'}}>21 Targets: </span>Each goal containing three
                                measurable targets, ensuring clear, actionable objectives for stakeholders.
                            </Typography>
                        </li>
                        <li>
                            <Typography sx={{fontSize: '20px', color: '#003C69',}}>
                                <span style={{fontWeight: 'bold'}}>81 Indicators: </span>Performance metrics that track
                                progress and measure impact in achieving the targets.
                            </Typography>
                        </li>
                        <li>
                            <Typography sx={{fontSize: '20px', color: '#003C69',}}>
                                <span style={{fontWeight: 'bold'}}>282 Items: </span>Data points from survey instruments
                                and sub-indicators that offer detailed insights into indicators.
                            </Typography>
                        </li>
                    </ul>
                </Box>

                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#F0605C',
                            color: '#fff',
                            borderRadius: '15px',
                            fontSize: '23px',
                            fontWeight: 'bold',
                            px: {xs: 0, sm: 7},
                            mt: 10,
                            textAlign: 'center',
                            '&:hover': {backgroundColor: '#F0605C'},
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <Typography component="span" sx={{fontWeight: 'bold', fontSize: '24px'}}>
                            DOWNLOAD:
                        </Typography>
                        <Typography component="span" sx={{fontWeight: '400', fontSize: '18px', textAlign: 'center'}}>
                            2025 Child Protection in Cyberspace Index Methodology Report
                        </Typography>
                    </Button>
                </Box>
            </Box>

            <Box sx={{display: "flex", flexDirection: "column", p: 4, gap: 2, mt: "40px", marginBottom: '150px',}}>
                <Box sx={{width: {xs: '100%', sm: "55%"}, textAlign: "center", color: "#003C69"}}>
                    <Typography variant="h4" fontWeight="bold">CPCI Indicator Navigator</Typography>
                    <Typography variant="body1">Click a goal, then click a target to see indicator details.</Typography>
                </Box>

                <Box sx={{display: "flex", flexDirection: {xs: 'column', sm: 'row'}, gap: 4}}>
                    {/* Left: Sunburst Chart */}
                    <Box sx={{
                        width: {xs: '100%', sm: "55%"},
                        height: {xs: 'auto', sm: "400px"},
                        mt: '1px',
                        ml: {sm: '30px'},
                        textAlign: "center",
                        position: "relative"
                    }}>
                        {displaySunburstRoot &&
                            <div className='sunburst-root-label'>
                                <span>CPCI Vision:</span>
                                <br/>Reduce<br/>Cyber-Risks<br/>by 15%
                            </div>
                        }
                        <SunburstHighChart/>
                    </Box>

                    {/* Right: Information Panel */}
                    <Box sx={{
                        width: {xs: '100%', sm: "45%"},
                        height: '677px',
                        overflow: 'auto',
                        p: 3,
                        border: "2px solid #003C69",
                        borderRadius: "8px"
                    }}>
                        <Typography variant="h4" fontWeight="bold"
                                    sx={{fontSize: '1.5rem', color: "#C89B2D", textAlign: 'center'}}>Prevalence of
                            Cyberbullying and Cybervictimization</Typography>
                        <Typography sx={{
                            fontSize: "16px",
                            color: "#C89B2D",
                            textAlign: 'center',
                            mt: 2
                        }}>CPCI.Target22.Ind01</Typography>

                        <Typography fontWeight="bold" sx={{color: "#003C69", mt: 2}}>Description:</Typography>
                        <Typography sx={{color: "#007DB4", textAlign: "left"}}>
                            The proportion of children and adolescents who have experienced cyberbullying and/or
                            cybervictimization in the past year.
                        </Typography>

                        <Typography sx={{color: "#003C69", fontWeight: 'bold', textAlign: "left", mt: 1}}>
                            The score of this indicator is developed by aggregating the scores of the following items:
                        </Typography>

                        <TableContainer component={Paper} sx={{my: 2, border: "1px solid #007DB4"}}>
                            <Table>
                                <TableHead sx={{backgroundColor: "#003C69"}}>
                                    <TableRow>
                                        <TableCell sx={{color: "white",}}>Items</TableCell>
                                        <TableCell sx={{color: "white",}}>Data Source</TableCell>
                                        <TableCell sx={{color: "white",}}>Organization</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Proportion of children and adolescents (age 8-18) who have experienced cyber
                                            bullying (%)
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            DQ Impact Tracker.<br/> 2024
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            DQ Institute
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Proportion of children and adolescents (age 8-18) who have experienced cyber
                                            victimization (%)
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            DQ Impact Tracker.<br/> 2024 </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            DQ Institute
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{textAlign: "left", color: "#003C69"}}>
                            <Typography variant="body1" fontWeight="bold">CPCI Research Partners</Typography>
                            <Typography variant="body1">These are our research collaborators who have contributed to
                                data collection, analysis, and research on topics related to this
                                indicator.</Typography>
                        </Box>
                        <TableContainer component={Paper} sx={{my: 2, border: "1px solid #003C69"}}>
                            <Table>
                                <TableHead sx={{backgroundColor: "#003C69"}}>
                                    <TableRow>
                                        <TableCell sx={{color: "white",}}>Research Project</TableCell>
                                        <TableCell sx={{color: "white",}}>Research Description</TableCell>
                                        <TableCell sx={{color: "white",}}>Contribution to the CPCI</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#00AF9B", textAlign: "left"}}>
                                            Research Project w/ Hyperlink
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#00AF9B", textAlign: "left"}}>
                                            Research Project w/ Hyperlink
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </TableCell>
                                        <TableCell
                                            sx={{border: "1px solid #003C69", color: "#003C69", textAlign: "left"}}>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default GlobalCPC;